import { memo, useCallback, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { appIconUrl } from '@renderer/utils/app-icon'
import type { ChatAttachment, FinalizedAttachment, PendingAttachment } from '../../../../types'
import { useTrackRemoveAttachment } from '../../../../hooks/attachments-hooks'
import { useChatImage } from '../../../../hooks/useChatImage'
import { useAttachmentStore } from '../../../../store'
import { PreviewableContext } from '../../input/context/previewable-context'
import { AttachmentStub } from '../attachment-stub'
import { useRemoveAttachment } from '../hooks/use-remove-attachment'
import type { AttachmentPillProps } from '../types'

export const Image = memo((attachment: AttachmentPillProps) => {
  const onRemove = useRemoveAttachment(attachment)
  const pendingImage = attachment.pending as Extract<PendingAttachment, { type: 'image' }>
  const attachedImage = attachment.attachment as Extract<FinalizedAttachment, { type: 'image' }>

  return (
    <PreviewableContext preview={<ImagePreview {...attachment} />}>
      <AttachmentStub
        label={pendingImage?.name ?? attachedImage?.fileName ?? 'Image'}
        image={pendingImage?.icon ?? pendingImage?.body ?? attachedImage?.fileId ?? attachedImage?.fileDataUrl}
        onRemove={onRemove}
      />
    </PreviewableContext>
  )
})
Image.displayName = 'Image'

export const ImagePreview = memo((attachment: ChatAttachment) => {
  const attachedImage = attachment.attachment as Extract<FinalizedAttachment, { type: 'image' }>
  const pendingImage = attachment.pending?.type === 'image' ? attachment.pending.body : undefined
  const image = pendingImage ?? attachedImage?.fileId ?? attachedImage?.fileDataUrl
  const shouldRequestImage = !(
    typeof image === 'string' &&
    (image.startsWith('data') || image.startsWith('http') || image.startsWith('blob'))
  )
  const { imageUrl, isImageLoading } = useChatImage(shouldRequestImage ? image : undefined)
  const [isImageLoaded, setIsImageLoaded] = useState(false)

  return (
    <div className="inline-block max-h-48 max-w-64 overflow-hidden rounded-2xl">
      {shouldRequestImage && (!imageUrl || isImageLoading) ? (
        <div className={'h-full w-full bg-white/10 object-cover'} />
      ) : (
        <img
          src={imageUrl ?? image}
          alt=""
          className={twMerge(
            'h-auto max-h-48 max-w-64 object-contain transition-transform',
            shouldRequestImage && !isImageLoaded ? 'opacity-0' : 'opacity-100',
          )}
          onLoad={() => setIsImageLoaded(true)}
        />
      )}
    </div>
  )
})
ImagePreview.displayName = 'ImagePreview'

/** A window capture pairs two attachments, so it renders from the image and reads its label from the contents. */
export const WindowContents = memo(
  ({
    windowContents,
    image,
    canRemove,
  }: {
    windowContents: Extract<FinalizedAttachment, { type: 'windowContents' }>
    image: Extract<FinalizedAttachment, { type: 'image' }>
    canRemove?: boolean
  }) => {
    const { removeAttachments } = useAttachmentStore()
    const trackRemoveAttachment = useTrackRemoveAttachment()

    // One pill, but up to two entries in the store. Detaching only the one the
    // pill was keyed on would leave its partner behind, rendering as a second
    // pill for the capture the user just removed. The standalone case passes a
    // synthesised image that matches no entry, so only the contents go.
    const onRemove = useCallback(() => {
      removeAttachments((candidate) => candidate.attachment !== image && candidate.attachment !== windowContents)
      trackRemoveAttachment({ type: 'windowContents', trigger: 'Attachment Pill' })
    }, [image, windowContents, removeAttachments, trackRemoveAttachment])

    return (
      <PreviewableContext preview={<ImagePreview {...{ attachment: image }} />}>
        <AttachmentStub
          label={windowContents.name ?? windowContents.appName}
          image={image.fileId ?? appIconUrl(windowContents.appName)}
          onRemove={canRemove ? onRemove : undefined}
        />
      </PreviewableContext>
    )
  },
)
WindowContents.displayName = 'WindowContents'
